import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { ChevronLeft, Search, PlusCircle, Edit2, Trash2, X, Filter, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GradeWithDetails, Student, Subject, Class } from '../../types/supabase';

type FilterParams = {
  studentId?: string;
  subjectId?: string;
  classId?: string;
  examType?: string;
};

type SubjectAverage = {
  id: string;
  name: string;
  average: number;
  count: number;
  coefficient: number;
  className?: string;
};

type StudentGradeEntry = {
  studentId: string;
  studentName: string;
  grade: string;
  comment: string;
  isAbsent: boolean;
};

const TrainerGrades = () => {
  const { user } = useAuth();
  const [grades, setGrades] = useState<GradeWithDetails[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterParams>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [subjectAverages, setSubjectAverages] = useState<SubjectAverage[]>([]);
  const [generalAverage, setGeneralAverage] = useState<number>(0);
  
  // Bulk add grade modal state
  const [showModal, setShowModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Class selection
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  
  // Step 2: Exam details
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>('');
  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [examInfo, setExamInfo] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0], // Today as default
    type: '',
    method: '',
    coefficient: '1'
  });
  
  // Step 3: Student grades
  const [studentGrades, setStudentGrades] = useState<StudentGradeEntry[]>([]);
  
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  // URL params handling
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const studentId = searchParams.get('student') || undefined;
    const subjectId = searchParams.get('subject') || undefined;
    const classId = searchParams.get('class') || undefined;
    const examType = searchParams.get('examType') || undefined;

    // Set initial filters from URL
    setFilters({
      studentId,
      subjectId,
      classId,
      examType
    });

    // If any filter is set, show the filter UI
    if (studentId || subjectId || classId || examType) {
      setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchGrades();
      fetchStudents();
      fetchSubjects();
      fetchClasses();
    }
  }, [user]);
  
  // Update averages when grades or filters change
  useEffect(() => {
    calculateAverages(filteredGrades);
  }, [grades, filters, searchTerm]);
  
  // Filter subjects by selected class
  useEffect(() => {
    if (selectedClassId) {
      const filtered = subjects.filter(
        subject => subject.class_id === selectedClassId || !subject.class_id
      );
      setFilteredSubjects(filtered);
      
      // Fetch students for the selected class
      fetchStudentsForClass(selectedClassId);
    } else {
      setFilteredSubjects(subjects);
    }
  }, [selectedClassId, subjects]);
  
  // Prepare student grade entries when moving to step 3
  useEffect(() => {
    if (currentStep === 3 && classStudents.length > 0) {
      const entries = classStudents.map(student => ({
        studentId: student.id,
        studentName: `${student.first_name} ${student.last_name}`,
        grade: '',
        comment: '',
        isAbsent: false
      }));
      setStudentGrades(entries);
    }
  }, [currentStep, classStudents]);

  const fetchStudentsForClass = async (classId: string) => {
    if (!classId) return;
    
    setIsLoadingStudents(true);
    
    try {
      // First get all students that belong to this class
      const { data: studentRecords, error: studentRecordsError } = await supabase
        .from('students')
        .select('*')
        .eq('class_id', classId)
        .order('last_name');
      
      if (studentRecordsError || !studentRecords || studentRecords.length === 0) {
        console.warn('No students found with direct class_id, trying subjects...');
        
        // Get all subjects for this class
        const { data: classSubjects, error: subjectsError } = await supabase
          .from('subjects')
          .select('id')
          .eq('class_id', classId);
        
        if (subjectsError) throw subjectsError;
        
        if (classSubjects && classSubjects.length > 0) {
          // Get all students who have grades in these subjects
          const { data: studentsWithGrades, error: gradesError } = await supabase
            .from('students')
            .select('*')
            .order('last_name');
          
          if (gradesError) throw gradesError;
          
          if (studentsWithGrades && studentsWithGrades.length > 0) {
            setClassStudents(studentsWithGrades);
            setFormError(null);
          } else {
            setFormError('Aucun étudiant trouvé dans cette classe');
            setClassStudents([]);
          }
        } else {
          setFormError('Aucune matière trouvée pour cette classe');
          setClassStudents([]);
        }
      } else {
        // We found students directly with class_id
        setClassStudents(studentRecords);
        setFormError(null);
      }
      
    } catch (error) {
      console.error('Error fetching students for class:', error);
      setFormError('Erreur lors du chargement des étudiants');
      setClassStudents([]);
    } finally {
      setIsLoadingStudents(false);
    }
  };

  const fetchGrades = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get grades assigned by this trainer with related info
      const { data, error } = await supabase
        .from('grades')
        .select(`
          *,
          student:students(id, first_name, last_name, email),
          subject:subjects(*, class:classes(id, name, class_number, period))
        `)
        .eq('trainer_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setGrades(data as GradeWithDetails[]);
    } catch (error) {
      console.error('Error fetching grades:', error);
      setError('Erreur lors du chargement des notes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .order('last_name');

      if (error) {
        throw error;
      }

      setStudents(data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subject_trainers')
        .select(`
          subject:subjects (
            id,
            name,
            description,
            coefficient,
            teaching_type,
            class:classes (
              id,
              name,
              class_number,
              period
            )
          )
        `)
        .eq('trainer_id', user?.id)
        .order('subject(name)');  // Fixed: Order by subject name correctly

      if (error) throw error;
      // Transform the data to match the expected format
      const transformedSubjects = data.map(item => item.subject);
      setSubjects(transformedSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setError('Erreur lors du chargement des matières');
    }
  };

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .order('name')
        .order('class_number');

      if (error) {
        throw error;
      }

      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };
  
  // Handle student grade updates in bulk entry form
  const handleStudentGradeChange = (studentId: string, field: 'grade' | 'comment' | 'isAbsent', value: string | boolean) => {
    setStudentGrades(prev => 
      prev.map(entry => 
        entry.studentId === studentId 
          ? { 
              ...entry, 
              [field]: value,
              // If marking as absent, clear grade
              ...(field === 'isAbsent' && value === true ? { grade: '' } : {})
            } 
          : entry
      )
    );
  };
  
  // Submit bulk grades
  const handleSubmitBulkGrades = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsSaving(true);

    try {
      // Validation for step 2 (already passed step 1)
      if (!selectedSubjectId) {
        setFormError("Veuillez sélectionner une matière");
        setIsSaving(false);
        return;
      }
      
      if (!examInfo.type) {
        setFormError("Veuillez sélectionner un type d'examen");
        setIsSaving(false);
        return;
      }
      
      // Check if at least one student has a grade or is marked absent
      const hasAnyGrades = studentGrades.some(entry => 
        entry.isAbsent === true || (entry.grade !== '' && parseFloat(entry.grade) >= 0 && parseFloat(entry.grade) <= 20)
      );
      
      if (!hasAnyGrades) {
        setFormError("Veuillez entrer au moins une note ou marquer un étudiant comme absent");
        setIsSaving(false);
        return;
      }

      // Get current user as the trainer
      const { data: userData } = await supabase.auth.getUser();
      const trainer_id = userData.user?.id;

      if (!trainer_id) {
        throw new Error("Impossible d'identifier le formateur");
      }

      // Prepare grade objects for all students with valid entries
      const gradesToInsert = studentGrades
        .filter(entry => entry.isAbsent || (entry.grade !== '' && !isNaN(parseFloat(entry.grade))))
        .map(entry => ({
          student_id: entry.studentId,
          subject_id: selectedSubjectId,
          trainer_id: trainer_id,
          grade: entry.isAbsent ? null : parseFloat(entry.grade),
          comment: entry.comment || null,
          is_absent: entry.isAbsent,
          exam_date: examInfo.date,
          exam_title: examInfo.title || null,
          coefficient: parseFloat(examInfo.coefficient) || 1,
          exam_type: examInfo.type,
          exam_method: examInfo.method || null
        }));

      if (gradesToInsert.length === 0) {
        setFormError("Aucune note valide à ajouter");
        setIsSaving(false);
        return;
      }

      // Insert all grades in a single operation
      const { data, error } = await supabase
        .from('grades')
        .insert(gradesToInsert)
        .select();

      if (error) {
        throw error;
      }

      // Refresh grades list
      await fetchGrades();
      
      // Close modal and reset form
      setShowModal(false);
      resetForm();
      
    } catch (error) {
      console.error('Error adding grades:', error);
      setFormError(`Erreur lors de l'ajout des notes: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGrade = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette note ?')) {
      setIsLoading(true);
      setError(null);
      
      try {
        const { error } = await supabase
          .from('grades')
          .delete()
          .eq('id', id);

        if (error) {
          throw error;
        }

        // Filter out the deleted grade from state
        setGrades(grades.filter(grade => grade.id !== id));
      } catch (error) {
        console.error('Error deleting grade:', error);
        setError('Erreur lors de la suppression de la note');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleFilterChange = (field: keyof FilterParams, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
    // Also clear URL params
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Helper function to render grade or absent status
  const renderGradeValue = (grade: GradeWithDetails) => {
    if (grade.is_absent) {
      return <span className="font-semibold text-gray-500">ABS</span>;
    } else {
      return (
        <span 
          className={`font-semibold ${
            (grade.grade || 0) >= 10 ? 'text-success-500' : 'text-error-500'
          }`}
        >
          {grade.grade}/20
        </span>
      );
    }
  };

  // Helper function to get exam type display text
  const getExamTypeText = (type: string | null) => {
    if (!type) return '-';
    
    const types: Record<string, string> = {
      'devoir_surveille': 'Devoir surveillé',
      'devoir_maison': 'Devoir maison',
      'participation': 'Participation',
      'examen_blanc': 'Examen blanc',
      'examen_officiel': 'Examen officiel'
    };
    
    return types[type] || type;
  };

  // Helper function to format class display
  const formatClassDisplay = (classObj: any) => {
    if (!classObj) return '-';
    return `${classObj.name} ${classObj.class_number} (${classObj.period})`;
  };

  // Function to calculate averages based on filtered grades
  const calculateAverages = (filteredGrades: GradeWithDetails[]) => {
    // Group grades by subject
    const subjectGrades: Record<string, GradeWithDetails[]> = {};
    const subjectMap: Record<string, {name: string, coefficient: number, className?: string}> = {};
    
    filteredGrades.forEach(grade => {
      if (!grade.is_absent) {
        if (!subjectGrades[grade.subject_id]) {
          subjectGrades[grade.subject_id] = [];
          subjectMap[grade.subject_id] = {
            name: grade.subject.name,
            coefficient: grade.subject.coefficient || 1,
            className: grade.subject.class ? formatClassDisplay(grade.subject.class) : undefined
          };
        }
        subjectGrades[grade.subject_id].push(grade);
      }
    });
    
    // Calculate averages for each subject
    const averages: SubjectAverage[] = [];
    
    Object.keys(subjectGrades).forEach(subjectId => {
      const grades = subjectGrades[subjectId];
      const subject = subjectMap[subjectId];
      
      let totalWeightedGrade = 0;
      let totalCoefficient = 0;
      
      grades.forEach(grade => {
        if (!grade.is_absent && grade.grade !== null) {
          const coef = grade.coefficient || 1;
          totalWeightedGrade += grade.grade * coef;
          totalCoefficient += coef;
        }
      });
      
      const average = totalCoefficient > 0 
        ? Math.round((totalWeightedGrade / totalCoefficient) * 10) / 10 
        : 0;
      
      averages.push({
        id: subjectId,
        name: subject.name,
        average,
        count: grades.length,
        coefficient: subject.coefficient,
        className: subject.className
      });
    });
    
    // Sort averages by subject name
    averages.sort((a, b) => a.name.localeCompare(b.name));
    
    // Calculate general average
    let totalWeightedAverage = 0;
    let totalCoefficient = 0;
    
    averages.forEach(subject => {
      totalWeightedAverage += subject.average * subject.coefficient;
      totalCoefficient += subject.coefficient;
    });
    
    const generalAvg = totalCoefficient > 0 
      ? Math.round((totalWeightedAverage / totalCoefficient) * 10) / 10 
      : 0;
    
    setSubjectAverages(averages);
    setGeneralAverage(generalAvg);
  };

  // Filters grades based on search term and applied filters
  const filteredGrades = grades.filter(grade => {
    const studentName = `${grade.student.first_name} ${grade.student.last_name}`.toLowerCase();
    const subjectName = grade.subject.name.toLowerCase();
    const examTitle = grade.exam_title?.toLowerCase() || '';
    
    // Basic text search
    const searchMatches = studentName.includes(searchTerm.toLowerCase()) || 
                         subjectName.includes(searchTerm.toLowerCase()) || 
                         examTitle.includes(searchTerm.toLowerCase());
    
    // Apply filters
    const studentMatches = !filters.studentId || grade.student_id === filters.studentId;
    const subjectMatches = !filters.subjectId || grade.subject_id === filters.subjectId;
    const classMatches = !filters.classId || 
                       (grade.subject.class && grade.subject.class.id === filters.classId);
    const examTypeMatches = !filters.examType || grade.exam_type === filters.examType;
    
    return searchMatches && studentMatches && subjectMatches && 
           classMatches && examTypeMatches;
  });
  
  const toggleGradeDetails = (gradeId: string) => {
    setSelectedGrade(selectedGrade === gradeId ? null : gradeId);
  };
  
  const toggleStatistics = () => {
    setShowStatistics(!showStatistics);
  };
  
  const handleNextStep = () => {
    setFormError(null);
    
    // Validation for step 1: Class selection
    if (currentStep === 1) {
      if (!selectedClassId) {
        setFormError("Veuillez sélectionner une classe");
        return;
      }
      setCurrentStep(2);
      return;
    }
    
    // Validation for step 2: Exam details
    if (currentStep === 2) {
      if (!selectedSubjectId) {
        setFormError("Veuillez sélectionner une matière");
        return;
      }
      
      if (!examInfo.type) {
        setFormError("Veuillez sélectionner un type d'examen");
        return;
      }
      
      setCurrentStep(3);
      return;
    }
  };
  
  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
    setFormError(null);
  };
  
  const resetForm = () => {
    setSelectedClassId('');
    setSelectedSubjectId('');
    setExamInfo({
      title: '',
      date: new Date().toISOString().split('T')[0],
      type: '',
      method: '',
      coefficient: '1'
    });
    setStudentGrades([]);
    setCurrentStep(1);
    setFormError(null);
  };
  
  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="animate-fade-in">
      <header className="flex justify-between items-center mb-8">
        <div>
          <Link to="/trainer" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
            <ChevronLeft className="h-4 w-4 mr-1" />
            <span>Retour au tableau de bord</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Gérer les notes</h1>
          <p className="mt-2 text-gray-600">
            Ajoutez, consultez et gérez les notes de vos étudiants
          </p>
        </div>
        
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary inline-flex items-center"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          Ajouter des notes
        </button>
      </header>

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-md mb-6">
          {error}
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="relative w-full md:w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="input-field pl-10"
            placeholder="Rechercher par étudiant, matière ou examen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-2 w-full md:w-auto">
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className={`btn ${showFilters ? 'btn-primary' : 'btn-outline'} flex items-center space-x-1`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtres avancés</span>
          </button>
          
          <button
            onClick={toggleStatistics}
            className={`btn ${showStatistics ? 'btn-primary' : 'btn-outline'} flex items-center space-x-1`}
          >
            <Sparkles className="h-4 w-4" />
            <span>Statistiques</span>
          </button>
          
          {Object.values(filters).some(value => value) && (
            <button 
              onClick={clearFilters}
              className="btn btn-outline flex items-center space-x-1 text-sm py-1 px-2"
            >
              <X className="h-4 w-4" />
              <span>Effacer les filtres</span>
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label htmlFor="studentFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Étudiant:
              </label>
              <select
                id="studentFilter"
                value={filters.studentId || ''}
                onChange={(e) => handleFilterChange('studentId', e.target.value)}
                className="input-field"
              >
                <option value="">Tous les étudiants</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.first_name} {student.last_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="subjectFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Matière:
              </label>
              <select
                id="subjectFilter"
                value={filters.subjectId || ''}
                onChange={(e) => handleFilterChange('subjectId', e.target.value)}
                className="input-field"
              >
                <option value="">Toutes les matières</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="classFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Classe:
              </label>
              <select
                id="classFilter"
                value={filters.classId || ''}
                onChange={(e) => handleFilterChange('classId', e.target.value)}
                className="input-field"
              >
                <option value="">Toutes les classes</option>
                {classes.map((classObj) => (
                  <option key={classObj.id} value={classObj.id}>
                    {classObj.name} {classObj.class_number} ({classObj.period})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="examTypeFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Type d'examen:
              </label>
              <select
                id="examTypeFilter"
                value={filters.examType || ''}
                onChange={(e) => handleFilterChange('examType', e.target.value)}
                className="input-field"
              >
                <option value="">Tous les types</option>
                <option value="devoir_surveille">Devoir surveillé</option>
                <option value="devoir_maison">Devoir maison</option>
                <option value="participation">Participation</option>
                <option value="examen_blanc">Examen blanc</option>
                <option value="examen_officiel">Examen officiel</option>
              </select>
            </div>
          </div>
        </div>
      )}
      
      {/* Statistics Panel */}
      {showStatistics && (
        <div className="bg-white rounded-lg shadow p-4 mb-6 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Statistiques des notes</h3>
            <div className="text-sm text-gray-500">
              {Object.values(filters).some(value => value) && (
                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-xs">
                  Filtres actifs
                </span>
              )}
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold">Moyenne générale</h4>
              <span 
                className={`text-xl font-bold ${
                  generalAverage >= 10 ? 'text-success-500' : 'text-error-500'
                }`}
              >
                {generalAverage}/20
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className={`h-2.5 rounded-full ${
                  generalAverage >= 10 ? 'bg-success-500' : 'bg-error-500'
                }`}
                style={{ width: `${(generalAverage / 20) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Calculée à partir des moyennes par matière, pondérées par leurs coefficients
            </p>
          </div>
          
          {subjectAverages.length > 0 ? (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <h4 className="font-semibold mb-3">Moyennes par matière</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subjectAverages.map(subject => (
                  <div key={subject.id} className="border rounded-md p-3">
                    <div className="mb-1">
                      <div className="font-medium">{subject.name}</div>
                      {subject.className && (
                        <div className="text-xs text-gray-500">{subject.className}</div>
                      )}
                    </div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600">
                        Coef. {subject.coefficient} ({subject.count} note{subject.count !== 1 ? 's' : ''})
                      </span>
                
                      <span 
                        className={`font-semibold ${
                          subject.average >= 10 ? 'text-success-500' : 'text-error-500'
                        }`}
                      >
                        {subject.average}/20
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          subject.average >= 10 ? 'bg-success-500' : 'bg-error-500'
                        }`}
                        style={{ width: `${(subject.average / 20) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              Aucune note disponible pour calculer les moyennes
            </div>
          )}
        </div>
      )}

      {/* Grades List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredGrades.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredGrades.map((grade) => (
              <div key={grade.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {grade.student.first_name} {grade.student.last_name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {grade.subject.name} - {formatClassDisplay(grade.subject.class)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    {renderGradeValue(grade)}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleGradeDetails(grade.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteGrade(grade.id)}
                        className="text-gray-400 hover:text-error-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {selectedGrade === grade.id && (
                  <div className="mt-4 pl-4 border-l-2 border-gray-200 space-y-2 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500">Type d'examen</p>
                        <p className="font-medium">{getExamTypeText(grade.exam_type)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="font-medium">{formatDate(grade.exam_date)}</p>
                      </div>
                      {grade.exam_title && (
                        <div>
                          <p className="text-gray-500">Titre</p>
                          <p className="font-medium">{grade.exam_title}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-500">Coefficient</p>
                        <p className="font-medium">{grade.coefficient || 1}</p>
                      </div>
                      {grade.comment && (
                        <div className="md:col-span-2">
                          <p className="text-gray-500">Commentaire</p>
                          <p className="font-medium">{grade.comment}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Aucune note trouvée</p>
          </div>
        )}
      </div>

      {/* Bulk Add Grade Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Ajouter des notes</h2>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {/* Progress Steps */}
              <div className="flex items-center mb-8">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex-1">
                    <div className={`flex items-center ${step !== 3 ? 'after:content-[""] after:h-0.5 after:w-full after:bg-gray-200 after:ml-2' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        currentStep === step ? 'bg-primary-500 text-white' :
                        currentStep > step ? 'bg-success-500 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {step}
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      {step === 1 ? 'Classe' : step === 2 ? 'Examen' : 'Notes'}
                    </p>
                  </div>
                ))}
              </div>
              
              {formError && (
                <div className="bg-error-50 text-error-700 p-4 rounded-md mb-6">
                  {formError}
                </div>
              )}
              
              <form onSubmit={handleSubmitBulkGrades}>
                {/* Step 1: Class Selection */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="class" className="block text-sm font-medium text-gray-700 mb-1">
                        Sélectionner une classe
                      </label>
                      <select
                        id="class"
                        value={selectedClassId}
                        onChange={(e) => setSelectedClassId(e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Choisir une classe</option>
                        {classes.map((classObj) => (
                          <option key={classObj.id} value={classObj.id}>
                            {classObj.name} {classObj.class_number} ({classObj.period})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                
                {/* Step 2: Exam Details */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Matière
                      </label>
                      <select
                        id="subject"
                        value={selectedSubjectId}
                        onChange={(e) => setSelectedSubjectId(e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Choisir une matière</option>
                        {filteredSubjects.map((subject) => (
                          <option key={subject.id} value={subject.id}>
                            {subject.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="examTitle" className="block text-sm font-medium text-gray-700 mb-1">
                        Titre de l'examen
                      </label>
                      <input
                        type="text"
                        id="examTitle"
                        value={examInfo.title}
                        onChange={(e) => setExamInfo({ ...examInfo, title: e.target.value })}
                        className="input-field"
                        placeholder="Ex: Contrôle continu 1"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="examDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Date de l'examen
                      </label>
                      <input
                        type="date"
                        id="examDate"
                        value={examInfo.date}
                        onChange={(e) => setExamInfo({ ...examInfo, date: e.target.value })}
                        className="input-field"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="examType" className="block text-sm font-medium text-gray-700 mb-1">
                        Type d'examen
                      </label>
                      <select
                        id="examType"
                        value={examInfo.type}
                        onChange={(e) => setExamInfo({ ...examInfo, type: e.target.value })}
                        className="input-field"
                        required
                      >
                        <option value="">Choisir un type</option>
                        <option value="devoir_surveille">Devoir surveillé</option>
                        <option value="devoir_maison">Devoir maison</option>
                        <option value="participation">Participation</option>
                        <option value="examen_blanc">Examen blanc</option>
                        <option value="examen_officiel">Examen officiel</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="examMethod" className="block text-sm font-medium text-gray-700 mb-1">
                        Méthode d'évaluation
                      </label>
                      <select
                        id="examMethod"
                        value={examInfo.method}
                        onChange={(e) => setExamInfo({ ...examInfo, method: e.target.value })}
                        className="input-field"
                      >
                        <option value="">Choisir une méthode</option>
                        <option value="ecrit">Écrit</option>
                        <option value="oral">Oral</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="coefficient" className="block text-sm font-medium text-gray-700 mb-1">
                        Coefficient
                      </label>
                      <input
                        type="number"
                        id="coefficient"
                        value={examInfo.coefficient}
                        onChange={(e) => setExamInfo({ ...examInfo, coefficient: e.target.value })}
                        className="input-field"
                        min="0.1"
                        step="0.1"
                        required
                      />
                    </div>
                  </div>
                )}
                
                {/* Step 3: Student Grades */}
                {currentStep === 3 && (
                  <div>
                    {isLoadingStudents ? (
                      <div className="text-center py-4">
                        Chargement des étudiants...
                      </div>
                    ) : studentGrades.length > 0 ? (
                      <div className="space-y-4">
                        {studentGrades.map((entry) => (
                          <div key={entry.studentId} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium">{entry.studentName}</h4>
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={entry.isAbsent}
                                  onChange={(e) => handleStudentGradeChange(entry.studentId, 'isAbsent', e.target.checked)}
                                  className="form-checkbox h-4 w-4 text-primary-600"
                                />
                                <span className="text-sm text-gray-600">Absent</span>
                              </label>
                            </div>
                            
                            {!entry.isAbsent && (
                              <div className="space-y-2">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Note sur 20
                                  </label>
                                  <input
                                    type="number"
                                    value={entry.grade}
                                    onChange={(e) => handleStudentGradeChange(entry.studentId, 'grade', e.target.value)}
                                    className="input-field"
                                    min="0"
                                    max="20"
                                    step="0.1"
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Commentaire
                                  </label>
                                  <input
                                    type="text"
                                    value={entry.comment}
                                    onChange={(e) => handleStudentGradeChange(entry.studentId, 'comment', e.target.value)}
                                    className="input-field"
                                    placeholder="Optionnel"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        Aucun étudiant trouvé dans cette classe
                      </div>
                    )}
                  </div>
                )}
                
                {/* Navigation Buttons */}
                <div className="mt-8 flex justify-between">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePreviousStep}
                      className="btn btn-outline"
                    >
                      Précédent
                    </button>
                  )}
                  
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="btn btn-primary ml-auto"
                    >
                      Suivant
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-primary ml-auto"
                      disabled={isSaving}
                    >
                      {isSaving ? 'Enregistrement...' : 'Enregistrer les notes'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerGrades;